import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '@/api/client'

interface Member {
  userId: number
  name: string
  email: string
}

interface RecipientSelectorProps {
  recipientType: 'all' | 'individual'
  onRecipientTypeChange: (type: 'all' | 'individual') => void
  selectedUsers: number[]
  onUsersChange: (ids: number[]) => void
}

export function RecipientSelector({
  recipientType,
  onRecipientTypeChange,
  selectedUsers,
  onUsersChange,
}: RecipientSelectorProps) {
  const { t } = useTranslation('messages')
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // 멤버 목록 로드
  useEffect(() => {
    if (recipientType === 'individual') {
      loadMembers()
    }
  }, [recipientType])

  const loadMembers = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/b2b/members', {
        params: { page: 0, size: 100 }
      })
      // API가 배열로 직접 반환
      const data = Array.isArray(response.data.data) ? response.data.data : (response.data.data.content || [])
      setMembers(data
        .filter((m: any) => m.role !== 'admin') // admin 제외
        .map((m: any) => ({
          userId: m.userId,
          name: m.fullName || m.name || t('recipient.noName'),
          email: m.email
        })))
    } catch (e) {
      console.error('Failed to load members:', e)
    } finally {
      setLoading(false)
    }
  }

  const toggleUser = (userId: number) => {
    if (selectedUsers.includes(userId)) {
      onUsersChange(selectedUsers.filter(id => id !== userId))
    } else {
      onUsersChange([...selectedUsers, userId])
    }
  }

  const filteredMembers = members.filter(m =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {t('recipient.label')}
      </label>

      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="recipientType"
            value="all"
            checked={recipientType === 'all'}
            onChange={() => onRecipientTypeChange('all')}
            className="text-blue-600"
          />
          <span>{t('recipient.allEmployees')}</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="recipientType"
            value="individual"
            checked={recipientType === 'individual'}
            onChange={() => onRecipientTypeChange('individual')}
            className="text-blue-600"
          />
          <span>{t('recipient.individual')}</span>
        </label>

        {recipientType === 'individual' && (
          <div className="ml-6 mt-2 border rounded-md p-3 bg-gray-50">
            <input
              type="text"
              placeholder={t('recipient.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm mb-2"
            />

            {loading ? (
              <p className="text-sm text-gray-500">{t('recipient.loading')}</p>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredMembers.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('recipient.noResults')}</p>
                ) : (
                  filteredMembers.map(member => (
                    <label
                      key={member.userId}
                      className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(member.userId)}
                        onChange={() => toggleUser(member.userId)}
                        className="text-blue-600"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium">{member.name}</span>
                        <span className="text-xs text-gray-500 ml-2">{member.email}</span>
                      </div>
                    </label>
                  ))
                )}
              </div>
            )}

            {selectedUsers.length > 0 && (
              <p className="text-sm text-blue-600 mt-2 font-medium">
                {t('recipient.selectedCount', { count: selectedUsers.length })}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
